import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import Button from '../../../../../../components/Button';
import Card from '../../../../../../components/Card';
import FlatButton from '../../../../../../components/FlatButton';
import PaymentMethodSelectionBox from '../../../../../../components/PaymentMethods/PaymentMethodSelectionBox';
import { useErrorToast } from '../../../../../../components/Toast';
import { H2, H5 } from '../../../../../../components/Typography';
import RentPaymentTypeEnum from '../../../../../../constants/enums/RentPaymentTypeEnum';
import { linkTo, routes } from '../../../../../../helpers/routes';
import { useGetActiveLeaseForRenter } from '../../../../../../hooks/useGetActiveLeaseForRenter';
import { EnumPaymentMethodStatusTypes } from '../../../../../../pages/enums';
import AddNewPaymentMethod from '../../../../../../pages/renters/payments/payment-methods/AddNewPaymentMethod';
import useSetPreferred from '../../../../../../pages/renters/settings/payments/autopay_settings/useSetPreferred';
import paymentMethodPropTypes from '../../../../../../prop-types/payment-method-prop-types';
import { segmentTracking } from '../../../../../../services/utilities';

import styles from './ExistingPaymentMethodForRentPayments.module.scss';

const ExistingPaymentMethodForRentPayments = ({
  paymentMethod,
  paymentMethods,
}) => {
  const [selected, setSelected] = useState(paymentMethod);
  const activeLease = useGetActiveLeaseForRenter();
  const history = useHistory();
  const showErrorMessage = useErrorToast();
  const { setPreferredForRentPayments, savingPreferredForRentPayments } =
    useSetPreferred(activeLease?.id);

  const onContinue = useCallback(async () => {
    try {
      const pm = paymentMethods.find((p) => p.id === selected);

      await setPreferredForRentPayments(pm);
      history.push(linkTo(routes.tenantOnboardingStepPaymentMethodSuccess));
    } catch {
      showErrorMessage('There was a problem saving your preferred option');
    }
  }, [selected, paymentMethods]);

  const onAdded = useCallback((pm, callback) => {
    const isNewBank =
      pm.type === RentPaymentTypeEnum.BANK_ACCOUNT &&
      pm.source_status === EnumPaymentMethodStatusTypes.NEW;
    if (!isNewBank) {
      setSelected(pm?.id);
    }
    callback();
  }, []);

  return (
    <Card padded className={styles.rpContainer}>
      <div className={styles.rpContent}>
        <H2 className="mb-16">How do you want to pay for rent?</H2>
        <H5 className="mb-16">Payment methods on file</H5>
        <PaymentMethodSelectionBox
          paymentMethods={paymentMethods}
          selected={selected}
          onChange={setSelected}
          hideDescription
          hideLabels
        />
        <AddNewPaymentMethod onAdded={onAdded} />
      </div>
      <div className={styles.buttons}>
        <Button
          className={styles.continueButton}
          variant="primary"
          onClick={onContinue}
          loading={savingPreferredForRentPayments}
          disabled={savingPreferredForRentPayments}
        >
          Continue
        </Button>
        <FlatButton
          className={styles.skipButton}
          to={linkTo(routes.tenantOnboardingStepRentersInsurance)}
          onClick={() => {
            segmentTracking('payment_method submit', {
              location: 'Tenant Onboarding Payment Method Page - Skip',
            });
          }}
        >
          Skip for now
        </FlatButton>
      </div>
    </Card>
  );
};
ExistingPaymentMethodForRentPayments.propTypes = {
  paymentMethod: paymentMethodPropTypes,
  paymentMethods: PropTypes.arrayOf(paymentMethodPropTypes),
};
export default ExistingPaymentMethodForRentPayments;
